import { useLanguage } from "../../../../context/LanguageContext";
import { Button } from "../../../../components/ui/button";
import { ArrowUpDown, Edit2, PlusCircle, Trash2 } from "lucide-react";
import { DataTable } from "../../../../components/DataTable";
import { useAuth } from "../../../../context/AuthContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../../lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Spin } from "antd";
import { Badge } from "../../../../components/ui/badge";
import MainButton from "../../../../components/MainButton";
import DialogDelete from "../../../../components/common/DialogDelete";
import TransactionDialog from "./TransactionDialog";

const TransactionsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [transactionData, setTransactionData] = useState({});
  const [errors, setErrors] = useState({});

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);

  const ApiUrl =
    user?.role === "admin"
      ? `transactions`
      : user?.role === "teacher" && `user/transactions/${user?.id}`;

  const fetchTransactions = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/${ApiUrl}`);
      setTransactions(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchTransactions();
  }, []);

  const handleTransaction = async (formData) => {
    setLoading(true);
    setErrors({});
    try {
      if (transactionData?.id) {
        await axios.put(
          `${BaseUrlApi}/transactions/${transactionData?.bid}`,
          formData
        );
        toast("Transactions saved", { status: "success" });
      } else {
        await axios.post(`${BaseUrlApi}/transactions`, formData);
        toast("Transactions added", { status: "success" });
      }
      await fetchTransactions();
      setOpen(false);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast("Error", { description: msg, status: "error" });
      setErrors(msg);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: "user.name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Teacher Name
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "month",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Month
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Amount
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ getValue }) => `$${getValue()}`,
    },
    {
      accessorKey: "reason",
      header: "Reason",
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ getValue }) => {
        const type = getValue();
        const label = type.charAt(0).toUpperCase() + type.slice(1);
        const variant = type === "deduction" ? "destructive" : "secondary";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
  ];

  if (user?.role === "admin") {
    columns.push({
      id: "actions",
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setOpen(true);
              setTransactionData(row.original);
            }}
          >
            <Edit2 />
          </Button>
          <DialogDelete
            apiLink={`/transactions/${row.original?.bid}`}
            onSuccess={fetchTransactions}
            itemTitle={row.original?.user?.name}
            name={"Transaction"}
          >
            <Button variant="ghost" size="icon">
              <Trash2 />
            </Button>
          </DialogDelete>
        </div>
      ),
    });
  }

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <h1 className="text-2xl font-bold">Transactions</h1>
        {user?.role === "admin" && (
          <MainButton
            Icon={PlusCircle}
            title={"Add Transaction"}
            onClick={() => {
              setTransactionData({});
              setOpen(true);
            }}
          />
        )}

        <TransactionDialog
          open={open}
          onOpenChange={setOpen}
          data={transactionData}
          saving={loading}
          onSave={handleTransaction}
          errors={errors}
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={transactions}
          title={"transactions by month & name"}
          filterNames={["month", "user_name"]}
        />
      )}
    </div>
  );
};

export default TransactionsPage;
